import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Home, Users, CreditCard, FileText, LogOut, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from '../../components/ui/sonner';
import * as api from '../../services/api';
import AccountsPage from './AccountsPage';
import ContactsPage from './ContactsPage';
import RIBPage from './RIBPage';
import BeneficiariesPage from './BeneficiariesPage';
import ProfilePage from './ProfilePage';
import Footer from '../../components/shared/Footer';

const BankingDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(user?.clientData);
  const [activeTab, setActiveTab] = useState('accueil');
  const [loading, setLoading] = useState(false);

  const refreshClientData = async () => {
    try {
      setLoading(true);
      const data = await api.getMyClientData(user.username);
      setClient(data);
    } catch (error) {
      console.error('Error refreshing client data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshClientData();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-lg text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const menuItems = [
    { id: 'accueil', label: 'Accueil', icon: Home },
    { id: 'contacts', label: 'Contacts', icon: Users },
    { id: 'rib', label: 'RIB', icon: CreditCard },
    { id: 'profil', label: 'Profil', icon: User }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              {/* Fortuneo Logo */}
              <img 
                src="https://customer-assets.emergentagent.com/job_fd99e742-6c59-4267-85f6-7d5b0b735f5d/artifacts/83jkoc66_IMG_0229.jpeg" 
                alt="Fortuneo" 
                className="h-12 object-contain"
              />
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Bienvenue, {client.firstName}</span>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <LogOut size={16} />
                <span>Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-grow w-full">
        {/* Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <nav className="flex space-x-1 p-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                    activeTab === item.id
                      ? 'bg-green-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div>
          {activeTab === 'accueil' && (
            <AccountsPage client={client} refreshClient={refreshClientData} />
          )}
          {activeTab === 'contacts' && (
            <ContactsPage client={client} />
          )}
          {activeTab === 'rib' && (
            <RIBPage client={client} />
          )}
          {activeTab === 'beneficiaires' && (
            <BeneficiariesPage client={client} refreshClient={refreshClientData} />
          )}
          {activeTab === 'profil' && (
            <ProfilePage client={client} onClientUpdate={refreshClientData} />
          )}
        </div>
      </div>

      <Footer
        showContact={true}
        onContactClick={() => {
          setActiveTab('contacts');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    </div>
  );
};

export default BankingDashboard;